/*
 * Talks to the ycnp.in website (Next.js) admin API.
 *
 * Local testing (npm start):
 *   /website-api  ->  src/setupProxy.js  ->  http://localhost:3000/api/admin-ext
 *   The secret key is added by setupProxy (from .env.development.local), never sent to the browser.
 *
 * Live (npm run build):
 *   ycnp.in/application/admin/api/website.php  (checks the owner login, then forwards with the key)
 */
import { useCallback } from "react";
import { useBaseUrl } from "../Contexts/BaseUrlContext";

const isLocal = process.env.NODE_ENV === "development";

export const SITE_URL = (process.env.REACT_APP_WEBSITE_URL || (isLocal ? "http://localhost:3000" : "https://ycnp.in")).replace(/\/+$/, "");

// "/uploads/x.jpg" -> "https://ycnp.in/uploads/x.jpg" (so images show inside the admin panel)
export const absUrl = (u) => {
  if (!u) return "";
  if (/^(https?:|data:|blob:)/.test(u)) return u;
  return SITE_URL + (u.startsWith("/") ? "" : "/") + u;
};

export const siteLink = (path = "/") => SITE_URL + path;

async function callLocal(payload) {
  const r = await fetch("/website-api", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  let d = null;
  try {
    d = await r.json();
  } catch (e) {
    throw new Error(r.status === 504 || r.status === 500 ? "वेबसाइट (VS Code) चालू नहीं है — वहाँ npm run dev चलाएँ" : "Server से जवाब नहीं आया");
  }
  if (r.status === 403) throw new Error("Admin key मेल नहीं खा रही — .env.development.local में WEBSITE_ADMIN_KEY जाँचें");
  if (!r.ok || (d && d.error)) throw new Error((d && d.error) || "Request failed");
  return d;
}

async function callLive(baseUrl, payload) {
  const f = new FormData();
  f.append("payload", JSON.stringify(payload));
  const r = await fetch(`${baseUrl}/website.php`, { method: "POST", body: f, credentials: "include" });
  const d = await r.json().catch(() => null);
  if (!d) throw new Error("Server से जवाब नहीं आया");
  if (!d.status) throw new Error(d.error || d.message || "Failed");
  if (d.data && d.data.error) throw new Error(d.data.error);
  return d.data;
}

export function useWebApi() {
  const { baseUrl } = useBaseUrl();
  return useCallback((action, params = {}) => {
    const payload = { action, ...params };
    return isLocal ? callLocal(payload) : callLive(baseUrl, payload);
  }, [baseUrl]);
}

// Read a File as base64 data URL (for image upload)
export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("File पढ़ी नहीं जा सकी"));
    r.readAsDataURL(file);
  });

// Shrinks big photos before upload (keeps PNG/GIF/SVG as they are)
export async function prepareImage(file, max = 1800) {
  if (!/^image\/(jpeg|webp)$/.test(file.type) || file.size < 400 * 1024) return fileToDataUrl(file);
  const url = await fileToDataUrl(file);
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale);
  c.height = Math.round(img.height * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", 0.86);
}
